import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { User, Edit, GraduationCap, School, Loader2 } from "lucide-react";

const Profile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showEditForm, setShowEditForm] = useState(false);

  const { athlete, isLoading } = useActiveAthlete();

  const [editForm, setEditForm] = useState<any>(null);

  const openEdit = () => {
    setEditForm({
      name: athlete?.name ?? "",
      sports: athlete?.sports ?? "",
      position: athlete?.position ?? "",
      graduationYear: athlete?.graduationYear ?? new Date().getFullYear() + 4,
      gpa: athlete?.gpa ?? "",
      school: athlete?.school ?? "",
      bio: athlete?.bio ?? "",
    });
    setShowEditForm(true);
  };

  const updateAthleteMutation = useMutation({
    mutationFn: async (data: typeof editForm) => {
      const res = await apiRequest("PUT", `/api/athletes/${athlete.id}`, data);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to update profile (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Profile updated", description: "Your athlete profile has been saved." });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes"] });
      setShowEditForm(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateAthleteMutation.mutate(editForm);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Page banner */}
      <div className="rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-4 py-4 shadow-lg shadow-black/25 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <User className="h-5 w-5 text-[#E0A458]" />
          <h1 className="font-montserrat font-bold text-xl text-white">Athlete Profile</h1>
        </div>
        {athlete && (
          <Button
            onClick={openEdit}
            className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
            size="sm"
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit Profile
          </Button>
        )}
      </div>

      {isLoading ? (
        <Card className="border border-[#E0A458]/20 bg-[#0D1B2A]">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-8 w-48 bg-[#1B263B]" />
            <Skeleton className="h-4 w-64 bg-[#1B263B]" />
            <Skeleton className="h-4 w-40 bg-[#1B263B]" />
            <Skeleton className="h-20 w-full bg-[#1B263B]" />
          </CardContent>
        </Card>
      ) : !athlete ? (
        <Card className="border border-[#E0A458]/20 bg-[#0D1B2A]">
          <CardContent className="p-8 text-center">
            <User className="h-12 w-12 text-[#E0A458]/50 mx-auto mb-3" />
            <p className="text-slate-300 font-semibold">No athlete profile yet</p>
            <p className="text-sm text-slate-400 mt-1">Go to your Dashboard to create an athlete profile.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-[#E0A458]/20 bg-[#0D1B2A]">
          <CardContent className="p-6 space-y-6">
            {/* Name + sport badges */}
            <div>
              <h2 className="font-montserrat font-bold text-2xl text-white mb-1">{athlete.name}</h2>
              <div className="flex flex-wrap gap-2 mt-2">
                {athlete.sports?.split(",").map((s: string) => (
                  <Badge
                    key={s.trim()}
                    className="bg-[#E0A458]/20 text-[#E0A458] border border-[#E0A458]/40"
                  >
                    {s.trim()}
                  </Badge>
                ))}
                {athlete.position && (
                  <Badge className="bg-[#1B263B] text-slate-300 border border-[#415A77]">
                    {athlete.position}
                  </Badge>
                )}
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {athlete.school && (
                <div className="flex items-center gap-2">
                  <School className="h-4 w-4 text-[#E0A458]/70 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">School</p>
                    <p className="text-sm text-slate-200 font-medium">{athlete.school}</p>
                  </div>
                </div>
              )}
              {athlete.graduationYear && (
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-[#E0A458]/70 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">Graduation</p>
                    <p className="text-sm text-slate-200 font-medium">Class of {athlete.graduationYear}</p>
                  </div>
                </div>
              )}
              {athlete.gpa != null && athlete.gpa !== "" && (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex items-center justify-center text-[#E0A458]/70 shrink-0 text-xs font-bold">
                    GPA
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">GPA</p>
                    <p className="text-sm text-slate-200 font-medium">{Number(athlete.gpa).toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Bio */}
            {athlete.bio && (
              <div>
                <p className="text-xs text-slate-400 mb-1 border-l-2 border-[#E0A458] pl-2">About</p>
                <p className="text-sm text-slate-300 leading-relaxed">{athlete.bio}</p>
              </div>
            )}

            {/* Account email */}
            <div className="pt-2 border-t border-[#1B263B]">
              <p className="text-xs text-slate-500">Account email: {(user as any)?.email}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit dialog */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0D1B2A] border border-[#E0A458]/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-montserrat">Edit Athlete Profile</DialogTitle>
          </DialogHeader>

          {editForm && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name" className="text-slate-300">Full Name</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sports" className="text-slate-300">Sports</Label>
                  <Input
                    id="edit-sports"
                    placeholder="e.g., Basketball, Baseball"
                    value={editForm.sports}
                    onChange={(e) => setEditForm({ ...editForm, sports: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-position" className="text-slate-300">Position</Label>
                  <Input
                    id="edit-position"
                    value={editForm.position}
                    onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-school" className="text-slate-300">School</Label>
                  <Input
                    id="edit-school"
                    value={editForm.school}
                    onChange={(e) => setEditForm({ ...editForm, school: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-grad-year" className="text-slate-300">Graduation Year</Label>
                  <Input
                    id="edit-grad-year"
                    type="number"
                    value={editForm.graduationYear}
                    onChange={(e) => setEditForm({ ...editForm, graduationYear: parseInt(e.target.value) })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-gpa" className="text-slate-300">GPA</Label>
                  <Input
                    id="edit-gpa"
                    type="number"
                    step="0.01"
                    min="0"
                    max="5.0"
                    value={editForm.gpa}
                    onChange={(e) => setEditForm({ ...editForm, gpa: parseFloat(e.target.value) })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-bio" className="text-slate-300">Bio</Label>
                <textarea
                  id="edit-bio"
                  className="flex min-h-24 w-full rounded-md border border-[#415A77] bg-[#1B263B] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#E0A458]"
                  placeholder="Brief description about the athlete"
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditForm(false)}
                  className="border-[#415A77] text-slate-300 hover:bg-[#1B263B]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
                  disabled={updateAthleteMutation.isPending}
                >
                  {updateAthleteMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
